import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiCalendar, FiPlus, FiEdit2, FiTrash2, FiClock,
  FiCheckCircle, FiXCircle, FiArrowRight, FiUsers, FiBarChart2,
  FiLock, FiHash, FiStar, FiTag,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import * as API from '../../services/api';
import styles from './EventsPage.module.css';

import { fmtCurrency } from '../../utils/formatNumber';

// -- Date helpers ---------------------------------------------------------------

const toLocalDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
};

const fmt = (d) =>
  toLocalDate(d)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) ?? '';

const localToday = () => new Date().toLocaleDateString('en-CA');

const getStatus = (event) => {
  const today = localToday();
  const start = (typeof event.contributionStartDate === 'string'
    ? event.contributionStartDate
    : event.contributionStartDate.toISOString()
  ).substring(0, 10);
  const end = (typeof event.contributionEndDate === 'string'
    ? event.contributionEndDate
    : event.contributionEndDate.toISOString()
  ).substring(0, 10);
  if (today < start) return 'upcoming';
  if (today > end) return 'ended';
  return 'active';
};

const toInput = (d) =>
  (typeof d === 'string' ? d : d.toISOString()).substring(0, 10);

const EMPTY_FORM = {
  title: '',
  description: '',
  eventDate: '',
  endDate: '',
  contributionStartDate: '',
  contributionEndDate: '',
};

// -- Status Badge --------------------------------------------------------------

const StatusBadge = ({ status }) => (
  <span className={`${styles.badge} ${styles[`badge_${status}`]}`}>
    {status === 'active'   && <FiCheckCircle size={11} />}
    {status === 'upcoming' && <FiClock size={11} />}
    {status === 'ended'    && <FiXCircle size={11} />}
    {status === 'active' ? 'Open Now' : status === 'upcoming' ? 'Upcoming' : 'Ended'}
  </span>
);

// -- Admin: Event Form (single-panel, no step wizard) --------------------------

const EventForm = ({ initial, derasarId, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [donations, setDonations] = useState([]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  useEffect(() => {
    let cancelled = false;
    const loadCats = async () => {
      try {
        const [catRes, existingRes] = await Promise.all([
          API.getDonationCategories(derasarId),
          initial?.id ? API.getEventDonations(initial.id, derasarId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setAllCategories((catRes.data || []).filter((c) => c.isActive));
        if (existingRes && Array.isArray(existingRes.data)) {
          setDonations(existingRes.data.map((ed) => ({
            donationCategoryId: String(ed.donationCategoryId),
            categoryName: ed.category?.name || String(ed.donationCategoryId),
            fixedRate: ed.fixedRate != null ? String(ed.fixedRate) : '',
            maxCount: ed.maxCount != null ? String(ed.maxCount) : '',
            isSpecial: !!ed.isSpecial,
            isExisting: true,
          })));
        }
      } catch {
        if (!cancelled) toast.error('Failed to load categories');
      } finally {
        if (!cancelled) setCatLoading(false);
      }
    };
    loadCats();
    return () => { cancelled = true; };
  }, [derasarId, initial?.id]);

  const toggleCategory = (cat) => {
    const id = String(cat.id);
    const existing = donations.find((d) => d.donationCategoryId === id);
    if (existing?.isExisting) return;
    if (existing) {
      setDonations((prev) => prev.filter((d) => d.donationCategoryId !== id));
    } else {
      setDonations((prev) => [...prev, { donationCategoryId: id, categoryName: cat.name, fixedRate: '', maxCount: '', isSpecial: false }]);
    }
  };

  const updateDonation = (id, field, value) => {
    setDonations((prev) =>
      prev.map((d) => {
        if (d.donationCategoryId !== id) return d;
        if (field === 'isSpecial' && value) return { ...d, isSpecial: true, fixedRate: '', maxCount: '' };
        return { ...d, [field]: value };
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.eventDate) return toast.error('Event date is required');
    if (!form.contributionStartDate) return toast.error('Contribution start date is required');
    if (!form.contributionEndDate) return toast.error('Contribution end date is required');
    if (form.contributionEndDate < form.contributionStartDate)
      return toast.error('Contribution end date must be after start date');
    setSaving(true);
    try {
      const donationsPayload = donations.map((d) => ({
        donationCategoryId: Number(d.donationCategoryId),
        fixedRate: d.isSpecial ? null : (d.fixedRate !== '' ? parseFloat(d.fixedRate) : null),
        maxCount: d.isSpecial ? null : (d.maxCount !== '' ? parseInt(d.maxCount, 10) : null),
        isSpecial: d.isSpecial,
      }));
      const payload = { ...form, derasarId, donations: donationsPayload };
      if (initial?.id) {
        await API.updateEvent(initial.id, payload);
        toast.success('Event updated');
      } else {
        await API.createEvent(payload);
        toast.success('Event created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.fullForm}>

      {/* Top two-column layout: details left, dates right */}
      <div className={styles.fullFormGrid}>

        {/* Left column: title + description */}
        <div className={styles.fullFormCard}>
          <div className={styles.fullFormCardTitle}><FiCalendar size={13} /> Event Details</div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Title *</label>
            <input className={styles.input} value={form.title} onChange={set('title')} placeholder="e.g. Paryushan 2025" />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Description</label>
            <textarea className={styles.textarea} value={form.description} onChange={set('description')} placeholder="Optional description..." rows={4} />
          </div>
        </div>

        {/* Right column: all four dates */}
        <div className={styles.fullFormCard}>
          <div className={styles.fullFormCardTitle}><FiClock size={13} /> Dates</div>
          <div className={styles.fullFormDateGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Event Date *</label>
              <input type="date" className={styles.input} value={form.eventDate} onChange={set('eventDate')} />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>End Date <span className={styles.labelOptional}>optional</span></label>
              <input type="date" className={styles.input} value={form.endDate} onChange={set('endDate')} min={form.eventDate || undefined} />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Contributions Start *</label>
              <input type="date" className={styles.input} value={form.contributionStartDate} onChange={set('contributionStartDate')} />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Contributions End *</label>
              <input type="date" className={styles.input} value={form.contributionEndDate} onChange={set('contributionEndDate')} min={form.contributionStartDate || undefined} />
            </div>
          </div>
        </div>
      </div>

      {/* Donation Categories — full width */}
      <div className={styles.fullFormCard}>
        <div className={styles.fullFormCardTitle}>
          <FiTag size={13} /> Donation Categories
          <span className={styles.labelOptional}>optional</span>
        </div>
        {catLoading ? (
          <div className={styles.panelLoading}><div className="spinner" /></div>
        ) : allCategories.length === 0 ? (
          <p className={styles.stepHint}>No active donation categories found.</p>
        ) : (
          <div className={styles.donationGrid}>
            {allCategories.map((cat) => {
              const id = String(cat.id);
              const selected = donations.find((d) => d.donationCategoryId === id);
              return (
                <div key={id} className={`${styles.donationRow} ${selected ? styles.donationRowSelected : ''}`}>
                  <label className={styles.donationCheck} style={selected?.isExisting ? { cursor: 'default' } : undefined}>
                    <input
                      type="checkbox"
                      checked={!!selected}
                      disabled={!!selected?.isExisting}
                      onChange={() => toggleCategory(cat)}
                    />
                    <span className={styles.donationName}>{cat.name}</span>
                    {selected?.isExisting && (
                      <FiLock size={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} title="Already saved - cannot remove" />
                    )}
                  </label>
                  {selected && (
                    <div className={styles.donationConfig}>
                      <label className={styles.specialToggle}>
                        <input
                          type="checkbox"
                          checked={selected.isSpecial}
                          onChange={(e) => updateDonation(id, 'isSpecial', e.target.checked)}
                        />
                        <FiStar size={12} /> Special (blind offer)
                      </label>
                      {!selected.isSpecial && (
                        <>
                          <label className={styles.configField}>
                            <FiLock size={12} /> Fixed Rate
                            <input type="number" min="0" step="0.01" className={styles.configInput}
                              placeholder="Any" value={selected.fixedRate}
                              onChange={(e) => updateDonation(id, 'fixedRate', e.target.value)} />
                          </label>
                          <label className={styles.configField}>
                            <FiHash size={12} /> Max
                            <input type="number" min="1" step="1" className={styles.configInput}
                              placeholder="Unlimited" value={selected.maxCount}
                              onChange={(e) => updateDonation(id, 'maxCount', e.target.value)} />
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.fullFormActions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" className={styles.btnPrimary} disabled={saving}>
          {saving ? 'Saving...' : initial?.id ? 'Update Event' : 'Create Event'}
        </button>
      </div>
    </form>
  );
};

// -- Special Donation Settle Modal ---------------------------------------------

const SpecialDonationSettleModal = ({ eventDonation, event, financialYears, onClose, onSettled }) => {
  const today = localToday();
  const selectableYears = financialYears.filter(
    (fy) => !fy.isClosed && (fy.isCurrent || (fy.endDate && fy.endDate.slice(0, 10) >= today))
  );

  const eventDate = (event?.eventDate ?? '').slice(0, 10);
  const defaultFy =
    (eventDate && selectableYears.find(
      (fy) => fy.startDate && fy.endDate &&
        eventDate >= fy.startDate.slice(0, 10) &&
        eventDate <= fy.endDate.slice(0, 10)
    )) ||
    selectableYears.find((fy) => fy.isCurrent) ||
    selectableYears[0];

  const [totalAmount, setTotalAmount] = useState('');
  const [financialYearId, setFinancialYearId] = useState(defaultFy?.id ? String(defaultFy.id) : '');
  const [settlementDate, setSettlementDate] = useState(localToday());

  const selectedFy = selectableYears.find((fy) => String(fy.id) === financialYearId) || null;
  const fyMinDate = selectedFy?.startDate ? selectedFy.startDate.slice(0, 10) : undefined;
  const fyMaxDate = selectedFy?.endDate   ? selectedFy.endDate.slice(0, 10)   : undefined;

  const handleYearChange = (id) => {
    setFinancialYearId(id);
    const fy = selectableYears.find((y) => String(y.id) === id);
    if (!fy) return;
    const min = fy.startDate?.slice(0, 10) ?? '';
    const max = fy.endDate?.slice(0, 10)   ?? '';
    setSettlementDate((prev) => {
      if (min && prev < min) return min;
      if (max && prev > max) return max;
      return prev;
    });
  };

  const [settling, setSettling] = useState(false);
  const registrationCount = eventDonation.registrations?.length || 0;
  const totalQuantity = eventDonation.totalQuantity
    ?? (eventDonation.registrations || []).reduce((s, r) => s + (r.quantity || 1), 0);

  const handleSettle = async () => {
    if (!totalAmount || parseFloat(totalAmount) <= 0) return toast.error('Enter a valid total amount');
    if (!financialYearId) return toast.error('Select a financial year');
    setSettling(true);
    try {
      await API.settleSpecialDonation(eventDonation.id, {
        totalAmount: parseFloat(totalAmount),
        financialYearId: Number(financialYearId),
        settlementDate,
      });
      toast.success(`Settled! ${registrationCount} registrant${registrationCount !== 1 ? 's' : ''}, ${fmtCurrency(parseFloat(totalAmount) / totalQuantity)} per slot.`);
      onSettled();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Settlement failed');
    } finally {
      setSettling(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <FiStar className={styles.modalIcon} />
          <h3>Settle Special Donation</h3>
        </div>
        <div className={styles.modalBody}>
          <p>
            <strong>{eventDonation.category?.name}</strong> — {registrationCount} registrant{registrationCount !== 1 ? 's' : ''},
            {' '}<strong>{totalQuantity}</strong> total offer slot{totalQuantity !== 1 ? 's' : ''}.
            Enter the total amount to divide by slots.
          </p>
          {totalQuantity > 0 && totalAmount && parseFloat(totalAmount) > 0 && (
            <p className={styles.perPersonHint}>
              Per slot: <strong>{fmtCurrency(parseFloat(totalAmount) / totalQuantity)}</strong>
              {registrationCount !== totalQuantity && (
                <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>
                  {' '}(amounts vary by quantity per registrant)
                </span>
              )}
            </p>
          )}
          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Total Amount *</label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              placeholder="e.g. 12000"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Financial Year *</label>
            <select value={financialYearId} onChange={(e) => handleYearChange(e.target.value)}>
              <option value="">Select year</option>
              {selectableYears.map((fy) => (
                <option key={fy.id} value={String(fy.id)}>{fy.yearLabel}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Settlement Date</label>
            <input
              type="date"
              value={settlementDate}
              min={fyMinDate}
              max={fyMaxDate}
              onChange={(e) => setSettlementDate(e.target.value)}
            />
          </div>
          <div className={styles.modalActions}>
            <button className={styles.btnSecondary} onClick={onClose} disabled={settling}>Cancel</button>
            <button className={styles.btnPrimary} onClick={handleSettle} disabled={settling || totalQuantity === 0}>
              {settling ? 'Settling...' : 'Create Offerings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// -- Donation chips helper (shared between hero and regular card) ---------------

const DonationChips = ({ eventDonations, hero }) => {
  if (!eventDonations || eventDonations.length === 0) return null;
  return (
    <div className={hero ? styles.heroDonationSection : styles.cardDonationSection}>
      <div className={hero ? styles.heroDonationLabel : styles.cardDonationLabel}>
        <FiTag size={11} /> Donation Categories
      </div>
      <div className={styles.donationChipRow}>
        {eventDonations.map((ed) => {
          const regCount = ed.registrations?.length || 0;
          const totalQty = ed.totalQuantity ?? (ed.registrations || []).reduce((s, r) => s + (r.quantity || 1), 0);
          return (
            <span
              key={ed.id}
              className={`${styles.donationChip} ${ed.isSpecial ? styles.donationChipSpecial : styles.donationChipRegular} ${hero ? styles.donationChipHero : ''}`}
            >
              {ed.isSpecial && <><FiStar size={10} /><span className={styles.chipSpecialTag}>Special</span></>}
              <span className={styles.chipName}>{ed.category?.name}</span>
              {ed.isSpecial && totalQty > 0 && (
                <span className={styles.chipBadge}>{regCount} reg / {totalQty} slots</span>
              )}
              {!ed.isSpecial && ed.fixedRate != null && (
                <span className={styles.chipBadge}>{fmtCurrency(ed.fixedRate)}</span>
              )}
              {!ed.isSpecial && ed.fixedRate == null && ed.maxCount != null && (
                <span className={styles.chipBadge}>max {ed.maxCount}</span>
              )}
              {!ed.isSpecial && ed.settledAt && (
                <span className={styles.chipSettled}>Settled</span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// -- Admin: Event Card ---------------------------------------------------------

const AdminEventCard = ({ event, onEdit, onDelete, onViewContributions, onNavigateOffer, onBlindOffer, onViewBlindOffers, financialYears }) => {
  const [summary, setSummary] = useState(null);
  const [eventDonations, setEventDonations] = useState([]);
  const [settleTarget, setSettleTarget] = useState(null);
  const status = getStatus(event);

  const loadData = useCallback(() => {
    API.getEventOfferSummary(event.id)
      .then((r) => setSummary(r.data))
      .catch(() => {});
    API.getEventDonations(event.id, event.derasarId)
      .then((r) => setEventDonations(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [event.id, event.derasarId]);

  useEffect(() => { loadData(); }, [loadData]);

  const specialDonations = eventDonations.filter((ed) => ed.isSpecial);
  const isHero = status === 'active';

  if (isHero) {
    return (
      <div className={styles.adminHeroCard}>
        <div className={styles.heroGlow} />
        <div className={styles.adminHeroContent}>

          {/* Top row: title + actions */}
          <div className={styles.adminHeroTop}>
            <div className={styles.adminHeroLeft}>
              <div className={styles.heroBadge}>Open for Contributions</div>
              <h2 className={styles.heroTitle}>{event.title}</h2>
              {event.description && <p className={styles.heroDesc}>{event.description}</p>}
            </div>
            <div className={styles.adminHeroActions}>
              <button className={styles.heroIconBtn} onClick={() => onEdit(event)} title="Edit">
                <FiEdit2 size={15} />
              </button>
              {toInput(event.contributionStartDate) > localToday() && (
                <button className={styles.heroIconBtnDanger} onClick={() => onDelete(event)} title="Delete">
                  <FiTrash2 size={15} />
                </button>
              )}
            </div>
          </div>

          {/* Meta */}
          <div className={styles.heroMeta}>
            <div className={styles.heroMetaItem}>
              <FiCalendar size={14} />
              <span>Event: <strong>{fmt(event.eventDate)}{event.endDate ? ` - ${fmt(event.endDate)}` : ''}</strong></span>
            </div>
            <div className={styles.heroMetaItem}>
              <FiClock size={14} />
              <span>Contributions open until <strong>{fmt(event.contributionEndDate)}</strong></span>
            </div>
          </div>

          {/* Stats */}
          <div className={styles.adminHeroStats}>
            <div className={styles.adminHeroStat}>
              <span className={styles.adminHeroStatValue}>{summary ? summary.memberCount : '-'}</span>
              <span className={styles.adminHeroStatLabel}>Members</span>
            </div>
            <div className={styles.adminHeroStat}>
              <span className={styles.adminHeroStatValue}>{summary ? summary.pledgeCount : '-'}</span>
              <span className={styles.adminHeroStatLabel}>Offers</span>
            </div>
            <div className={styles.adminHeroStat}>
              <span className={styles.adminHeroStatValue}>{summary ? fmtCurrency(summary.total) : '-'}</span>
              <span className={styles.adminHeroStatLabel}>Total Offered</span>
            </div>
          </div>

          {/* Donation category chips */}
          <DonationChips eventDonations={eventDonations} hero />

          {/* Buttons */}
          <div className={styles.heroBtnRow}>
            <button className={styles.heroBtn} onClick={() => onNavigateOffer(event)}>
              New Offer <FiArrowRight size={16} />
            </button>
            {specialDonations.length > 0 && (
              <button className={styles.heroBlindOfferBtn} onClick={() => onBlindOffer(event)}>
                <FiStar size={15} /> Blind Offer
              </button>
            )}
            {specialDonations.length > 0 && (
              <button className={styles.heroViewBlindBtn} onClick={() => onViewBlindOffers(event)}>
                <FiStar size={15} /> View Blind Offers
              </button>
            )}
            <button className={styles.contribBtn} onClick={() => onViewContributions(event.id)}>
              <FiBarChart2 size={13} /> View Contributions
            </button>
          </div>

          {/* Special donations settle */}
          {specialDonations.length > 0 && (
            <div className={styles.adminHeroSpecial}>
              {specialDonations.map((ed) => {
                const edTotalQty = ed.totalQuantity ?? (ed.registrations || []).reduce((s, r) => s + (r.quantity || 1), 0);
                const edCount = ed.registrations?.length || 0;
                return (
                  <div key={ed.id} className={styles.adminHeroSpecialRow}>
                    <span className={styles.adminHeroSpecialName}><FiStar size={11} /> {ed.category?.name}</span>
                    <span className={styles.adminHeroSpecialCount}>
                      {edCount} registrant{edCount !== 1 ? 's' : ''} &middot; {edTotalQty} slot{edTotalQty !== 1 ? 's' : ''}
                    </span>
                    {!ed.settledAt ? (
                      <button className={styles.adminHeroSettleBtn} onClick={() => setSettleTarget(ed)}>
                        Settle
                      </button>
                    ) : (
                      <span className={styles.adminHeroSettledBadge}>&#10003; Settled</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {settleTarget && (
          <SpecialDonationSettleModal
            eventDonation={settleTarget}
            event={event}
            financialYears={financialYears}
            onClose={() => setSettleTarget(null)}
            onSettled={() => { setSettleTarget(null); loadData(); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.adminCard} ${styles[`card_${status}`]}`}>
      <div className={styles.adminCardTop}>
        <div>
          <StatusBadge status={status} />
          <h3 className={styles.cardTitle}>{event.title}</h3>
          {event.description && <p className={styles.cardDesc}>{event.description}</p>}
        </div>
        <div className={styles.adminCardActions}>
          <button className={styles.iconBtn} onClick={() => onEdit(event)} title="Edit">
            <FiEdit2 size={15} />
          </button>
          {toInput(event.contributionStartDate) > localToday() && (
            <button className={styles.iconBtnDanger} onClick={() => onDelete(event)} title="Delete">
              <FiTrash2 size={15} />
            </button>
          )}
        </div>
      </div>

      <div className={styles.adminCardMeta}>
        <div className={styles.metaItem}>
          <FiCalendar size={13} />
          <span>Event: <strong>{fmt(event.eventDate)}{event.endDate ? ` - ${fmt(event.endDate)}` : ''}</strong></span>
        </div>
        <div className={styles.metaItem}>
          <FiClock size={13} />
          <span>
            Contributions: <strong>{fmt(event.contributionStartDate)}</strong>
            {' '}-{' '}
            <strong>{fmt(event.contributionEndDate)}</strong>
          </span>
        </div>
      </div>

      <div className={styles.adminCardStats}>
        <div className={styles.statBox}>
          <span className={styles.statLabel}>Members</span>
          <span className={styles.statValue}>{summary ? summary.memberCount : '-'}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statLabel}>Offers</span>
          <span className={styles.statValue}>{summary ? summary.pledgeCount : '-'}</span>
        </div>
        <div className={styles.statBox}>
          <span className={styles.statLabel}>Total Offered</span>
          <span className={styles.statValue}>{summary ? fmtCurrency(summary.total) : '-'}</span>
        </div>
      </div>

      {/* Donation category chips */}
      <DonationChips eventDonations={eventDonations} hero={false} />

      <div className={styles.adminCardFooter}>
        {specialDonations.length > 0 && (
          <button className={styles.viewBlindOffersBtn} onClick={() => onViewBlindOffers(event)}>
            <FiStar size={13} /> View Blind Offers
          </button>
        )}
        <button className={styles.contribBtn} onClick={() => onViewContributions(event.id)}>
          <FiBarChart2 size={13} /> View Contributions
        </button>

        {specialDonations.length > 0 && (
          <div className={styles.specialSection}>
            {specialDonations.map((ed) => {
              const edTotalQty = ed.totalQuantity ?? (ed.registrations || []).reduce((s, r) => s + (r.quantity || 1), 0);
              const edCount = ed.registrations?.length || 0;
              return (
                <div key={ed.id} className={styles.specialRow}>
                  <span className={styles.specialName}><FiStar size={11} /> {ed.category?.name}</span>
                  <span className={styles.specialCount}>
                    {edCount} registrant{edCount !== 1 ? 's' : ''} &middot; {edTotalQty} slot{edTotalQty !== 1 ? 's' : ''}
                  </span>
                  {!ed.settledAt ? (
                    <button className={styles.settleBtn} onClick={() => setSettleTarget(ed)}>
                      Settle
                    </button>
                  ) : (
                    <span className={styles.settledBadge}>&#10003; Settled</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {settleTarget && (
        <SpecialDonationSettleModal
          eventDonation={settleTarget}
          financialYears={financialYears}
          onClose={() => setSettleTarget(null)}
          onSettled={() => { setSettleTarget(null); loadData(); }}
        />
      )}
    </div>
  );
};

// -- User: Active Event Hero Card ----------------------------------------------

const ActiveHeroCard = ({ event, onNavigateOffer, onBlindOffer, onViewBlindOffers, onViewContributions }) => (
  <div className={styles.heroCard}>
    <div className={styles.heroGlow} />
    <div className={styles.heroContent}>
      <div className={styles.heroBadge}>Open for Contributions</div>
      <h2 className={styles.heroTitle}>{event.title}</h2>
      {event.description && <p className={styles.heroDesc}>{event.description}</p>}
      <div className={styles.heroMeta}>
        <div className={styles.heroMetaItem}>
          <FiCalendar size={14} />
          <span>Event: <strong>{fmt(event.eventDate)}{event.endDate ? ` - ${fmt(event.endDate)}` : ''}</strong></span>
        </div>
        <div className={styles.heroMetaItem}>
          <FiClock size={14} />
          <span>Open until <strong>{fmt(event.contributionEndDate)}</strong></span>
        </div>
      </div>
      <div className={styles.heroBtnRow}>
        <button className={styles.heroBtn} onClick={onNavigateOffer}>
          New Offer <FiArrowRight size={16} />
        </button>
        <button className={styles.heroBlindOfferBtn} onClick={onBlindOffer}>
          <FiStar size={15} /> Blind Offer
        </button>
        <button className={styles.heroViewBlindBtn} onClick={onViewBlindOffers}>
          <FiStar size={15} /> View Blind Offers
        </button>
        <button className={styles.contribBtn} onClick={onViewContributions}>
          <FiBarChart2 size={13} /> View Contributions
        </button>
      </div>
    </div>
  </div>
);

// -- User: Regular Event Card --------------------------------------------------

const UserEventCard = ({ event, onNavigateOffer, onBlindOffer, onViewBlindOffers, onViewContributions }) => {
  const status = getStatus(event);
  return (
    <div className={`${styles.userCard} ${styles[`card_${status}`]}`}>
      <div className={styles.userCardHeader}>
        <StatusBadge status={status} />
        <div className={styles.userCardDate}>
          {fmt(event.eventDate)}{event.endDate ? ` - ${fmt(event.endDate)}` : ''}
        </div>
      </div>
      <h4 className={styles.userCardTitle}>{event.title}</h4>
      {event.description && <p className={styles.userCardDesc}>{event.description}</p>}
      <div className={styles.userCardWindow}>
        <FiClock size={12} />
        {fmt(event.contributionStartDate)} - {fmt(event.contributionEndDate)}
      </div>
      <div className={styles.userCardBtnRow}>
        {status === 'active' && (
          <button className={styles.offerSmallBtn} onClick={onNavigateOffer}>
            <FiArrowRight size={13} /> New Offer
          </button>
        )}
        {status === 'active' && (
          <button className={styles.blindOfferSmallBtn} onClick={onBlindOffer}>
            <FiStar size={13} /> Blind Offer
          </button>
        )}
        <button className={styles.viewBlindSmallBtn} onClick={onViewBlindOffers}>
          <FiStar size={13} /> View Blind Offers
        </button>
        <button className={styles.contribSmallBtn} onClick={onViewContributions}>
          <FiBarChart2 size={13} /> Contributions
        </button>
      </div>
    </div>
  );
};

// -- Main Page -----------------------------------------------------------------

const EventsPage = () => {
  const { activeDerasarId, isDerasarAdmin } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const res = await API.getEvents(activeDerasarId);
      setEvents(res.data);
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeDerasarId) return;
    API.getFinancialYears(activeDerasarId)
      .then((r) => setFinancialYears(r.data || []))
      .catch(() => {});
  }, [activeDerasarId]);

  const handleEdit = (event) => {
    setEditTarget({
      ...event,
      eventDate: toInput(event.eventDate),
      endDate: event.endDate ? toInput(event.endDate) : '',
      contributionStartDate: toInput(event.contributionStartDate),
      contributionEndDate: toInput(event.contributionEndDate),
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await API.deleteEvent(deleteTarget.id);
      toast.success('Event removed');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditTarget(null);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const activeEvents = events.filter((e) => getStatus(e) === 'active');
  const otherEvents = events.filter((e) => getStatus(e) !== 'active');

  const makeCardProps = (ev) => ({
    event: ev,
    onEdit: handleEdit,
    onDelete: setDeleteTarget,
    onViewContributions: (id) => navigate(`/derasar/events/${id}/contributions`),
    financialYears,
    onNavigateOffer: (event) =>
      navigate('/accounting/offerings', {
        state: {
          autoOpenForm: true,
          offeringDate: toInput(event.eventDate),
          endDate: event.endDate ? toInput(event.endDate) : null,
          isEventDay: true,
          lockDate: !event.endDate,
          eventId: event.id,
        },
      }),
    onBlindOffer: (event) => navigate(`/derasar/events/${event.id}/blind-offer`, { state: { event } }),
    onViewBlindOffers: (event) => navigate(`/derasar/events/${event.id}/blind-offers`, { state: { event } }),
  });

  // -- Admin View --------------------------------------------------------------
  if (isDerasarAdmin) {

    const eventCards = (
      <>
        {events.length === 0 && !showForm && (
          <div className={styles.emptyState}>
            <FiCalendar size={40} />
            <p>No events yet. Add your first event above.</p>
          </div>
        )}
        {activeEvents.length > 0 && (
          <div className={styles.adminHeroSection}>
            {activeEvents.map((ev) => <AdminEventCard key={ev.id} {...makeCardProps(ev)} />)}
          </div>
        )}
        {otherEvents.length > 0 && (
          <div className={styles.adminGrid}>
            {otherEvents.map((ev) => <AdminEventCard key={ev.id} {...makeCardProps(ev)} />)}
          </div>
        )}
      </>
    );

    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h2 className={styles.pageTitle}>Derasar Events</h2>
            <p className={styles.pageSubtitle}>Manage important days and track member contributions</p>
          </div>
          <button className={styles.btnPrimary} onClick={() => { setEditTarget(null); setShowForm(true); }}>
            <FiPlus size={15} /> Add Event
          </button>
        </div>

        {showForm ? (
          <div className={styles.formPage}>
            <div className={styles.formPageHeader}>
              <button className={styles.formPageBack} onClick={closeForm}>
                <FiXCircle size={16} /> Cancel
              </button>
              <div className={styles.formPageTitle}>
                <FiCalendar size={18} />
                {editTarget?.id ? 'Edit Event' : 'Add New Event'}
              </div>
            </div>
            <EventForm
              key={editTarget?.id ?? 'new'}
              initial={editTarget}
              derasarId={activeDerasarId}
              onSave={() => { closeForm(); load(); }}
              onCancel={closeForm}
            />
          </div>
        ) : (
          eventCards
        )}

        {deleteTarget && (
          <div className={styles.modalOverlay} onClick={() => setDeleteTarget(null)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <FiTrash2 className={styles.modalIconDanger} />
                <h3>Remove Event</h3>
              </div>
              <div className={styles.modalBody}>
                <p>Remove <strong>"{deleteTarget.title}"</strong>? This cannot be undone.</p>
                <div className={styles.modalActions}>
                  <button className={styles.btnSecondary} onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button className={styles.btnDanger} onClick={handleDelete}>Remove</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -- User View ---------------------------------------------------------------
  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Derasar Events</h2>
          <p className={styles.pageSubtitle}>Important days and contribution opportunities</p>
        </div>
        <button className={styles.btnSecondary} onClick={() => navigate('/accounting/offerings')}>
          <FiUsers size={14} /> View My Offerings
        </button>
      </div>

      {activeEvents.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Open for Contributions</div>
          <div className={styles.heroGrid}>
            {activeEvents.map((ev) => (
              <ActiveHeroCard
                key={ev.id}
                event={ev}
                onNavigateOffer={() =>
                  navigate('/accounting/offerings', {
                    state: {
                      autoOpenForm: true,
                      offeringDate: toInput(ev.eventDate),
                      endDate: ev.endDate ? toInput(ev.endDate) : null,
                      isEventDay: true,
                      lockDate: !ev.endDate,
                      eventId: ev.id,
                    },
                  })
                }
                onBlindOffer={() => navigate(`/derasar/events/${ev.id}/blind-offer`, { state: { event: ev } })}
                onViewBlindOffers={() => navigate(`/derasar/events/${ev.id}/blind-offers`, { state: { event: ev } })}
                onViewContributions={() => navigate(`/derasar/events/${ev.id}/contributions`)}
              />
            ))}
          </div>
        </section>
      )}

      {otherEvents.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionLabel}>All Events This Year</div>
          <div className={styles.userGrid}>
            {otherEvents.map((ev) => (
              <UserEventCard
                key={ev.id}
                event={ev}
                onNavigateOffer={() =>
                  navigate('/accounting/offerings', {
                    state: {
                      autoOpenForm: true,
                      offeringDate: toInput(ev.eventDate),
                      endDate: ev.endDate ? toInput(ev.endDate) : null,
                      isEventDay: true,
                      lockDate: !ev.endDate,
                      eventId: ev.id,
                    },
                  })
                }
                onBlindOffer={() => navigate(`/derasar/events/${ev.id}/blind-offer`, { state: { event: ev } })}
                onViewBlindOffers={() => navigate(`/derasar/events/${ev.id}/blind-offers`, { state: { event: ev } })}
                onViewContributions={() => navigate(`/derasar/events/${ev.id}/contributions`)}
              />
            ))}
          </div>
        </section>
      )}

      {events.length === 0 && (
        <div className={styles.emptyState}>
          <FiCalendar size={40} />
          <p>No events scheduled yet. Check back soon!</p>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
